import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Navigation, Users, Building2, Car, Plane, AlertCircle } from 'lucide-react';

// API URL for geocode proxy (to avoid CORS issues with Nominatim)
const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || ''}/api`;

// Fix for default markers in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Major airports with coordinates
const AIRPORTS = [
  // Poland
  { code: 'WAW', name: 'Warsaw Chopin', city: 'Warsaw', country: 'Poland', lat: 52.1657, lon: 20.9671 },
  { code: 'WMI', name: 'Warsaw Modlin', city: 'Warsaw', country: 'Poland', lat: 52.4511, lon: 20.6518 },
  { code: 'KRK', name: 'Kraków Balice', city: 'Kraków', country: 'Poland', lat: 50.0777, lon: 19.7848 },
  { code: 'GDN', name: 'Gdańsk Lech Wałęsa', city: 'Gdańsk', country: 'Poland', lat: 54.3776, lon: 18.4662 },
  { code: 'WRO', name: 'Wrocław Copernicus', city: 'Wrocław', country: 'Poland', lat: 51.1027, lon: 16.8858 },
  { code: 'POZ', name: 'Poznań Ławica', city: 'Poznań', country: 'Poland', lat: 52.4210, lon: 16.8263 },
  { code: 'KTW', name: 'Katowice Pyrzowice', city: 'Katowice', country: 'Poland', lat: 50.4743, lon: 19.0800 },
  { code: 'SZZ', name: 'Szczecin Goleniów', city: 'Szczecin', country: 'Poland', lat: 53.5847, lon: 14.9022 },
  { code: 'RZE', name: 'Rzeszów Jasionka', city: 'Rzeszów', country: 'Poland', lat: 50.1100, lon: 22.0190 },
  { code: 'LCJ', name: 'Łódź Lublinek', city: 'Łódź', country: 'Poland', lat: 51.7219, lon: 19.3981 },
  { code: 'BZG', name: 'Bydgoszcz Szwederowo', city: 'Bydgoszcz', country: 'Poland', lat: 53.0968, lon: 17.9777 },
  // Germany
  { code: 'BER', name: 'Berlin Brandenburg', city: 'Berlin', country: 'Germany', lat: 52.3667, lon: 13.5033 },
  { code: 'FRA', name: 'Frankfurt', city: 'Frankfurt', country: 'Germany', lat: 50.0379, lon: 8.5622 },
  { code: 'MUC', name: 'Munich', city: 'Munich', country: 'Germany', lat: 48.3538, lon: 11.7861 },
  { code: 'HAM', name: 'Hamburg', city: 'Hamburg', country: 'Germany', lat: 53.6304, lon: 9.9882 },
  { code: 'DUS', name: 'Düsseldorf', city: 'Düsseldorf', country: 'Germany', lat: 51.2895, lon: 6.7668 },
  { code: 'CGN', name: 'Cologne Bonn', city: 'Cologne', country: 'Germany', lat: 50.8659, lon: 7.1427 },
  { code: 'STR', name: 'Stuttgart', city: 'Stuttgart', country: 'Germany', lat: 48.6899, lon: 9.2220 },
  // France
  { code: 'CDG', name: 'Paris Charles de Gaulle', city: 'Paris', country: 'France', lat: 49.0097, lon: 2.5479 },
  { code: 'ORY', name: 'Paris Orly', city: 'Paris', country: 'France', lat: 48.7262, lon: 2.3652 },
  { code: 'NCE', name: 'Nice Côte d\'Azur', city: 'Nice', country: 'France', lat: 43.6584, lon: 7.2159 },
  { code: 'LYS', name: 'Lyon Saint-Exupéry', city: 'Lyon', country: 'France', lat: 45.7256, lon: 5.0811 },
  { code: 'MRS', name: 'Marseille Provence', city: 'Marseille', country: 'France', lat: 43.4393, lon: 5.2214 },
  { code: 'TLS', name: 'Toulouse Blagnac', city: 'Toulouse', country: 'France', lat: 43.6293, lon: 1.3638 },
  // Netherlands/Belgium
  { code: 'AMS', name: 'Amsterdam Schiphol', city: 'Amsterdam', country: 'Netherlands', lat: 52.3105, lon: 4.7683 },
  { code: 'BRU', name: 'Brussels', city: 'Brussels', country: 'Belgium', lat: 50.9014, lon: 4.4844 },
  { code: 'CRL', name: 'Brussels South Charleroi', city: 'Charleroi', country: 'Belgium', lat: 50.4592, lon: 4.4538 },
  // UK
  { code: 'LHR', name: 'London Heathrow', city: 'London', country: 'UK', lat: 51.4700, lon: -0.4543 },
  { code: 'LGW', name: 'London Gatwick', city: 'London', country: 'UK', lat: 51.1537, lon: -0.1821 },
  { code: 'STN', name: 'London Stansted', city: 'London', country: 'UK', lat: 51.8860, lon: 0.2389 },
  { code: 'LTN', name: 'London Luton', city: 'London', country: 'UK', lat: 51.8747, lon: -0.3683 },
  { code: 'MAN', name: 'Manchester', city: 'Manchester', country: 'UK', lat: 53.3537, lon: -2.2750 },
  { code: 'EDI', name: 'Edinburgh', city: 'Edinburgh', country: 'UK', lat: 55.9500, lon: -3.3725 },
  { code: 'BHX', name: 'Birmingham', city: 'Birmingham', country: 'UK', lat: 52.4539, lon: -1.7480 },
  // Nordic
  { code: 'CPH', name: 'Copenhagen', city: 'Copenhagen', country: 'Denmark', lat: 55.6180, lon: 12.6560 },
  { code: 'OSL', name: 'Oslo Gardermoen', city: 'Oslo', country: 'Norway', lat: 60.1939, lon: 11.1004 },
  { code: 'BGO', name: 'Bergen Flesland', city: 'Bergen', country: 'Norway', lat: 60.2934, lon: 5.2181 },
  { code: 'ARN', name: 'Stockholm Arlanda', city: 'Stockholm', country: 'Sweden', lat: 59.6519, lon: 17.9186 },
  { code: 'GOT', name: 'Gothenburg Landvetter', city: 'Gothenburg', country: 'Sweden', lat: 57.6628, lon: 12.2798 },
  { code: 'HEL', name: 'Helsinki Vantaa', city: 'Helsinki', country: 'Finland', lat: 60.3172, lon: 24.9633 },
  // Spain
  { code: 'MAD', name: 'Madrid Barajas', city: 'Madrid', country: 'Spain', lat: 40.4983, lon: -3.5676 },
  { code: 'BCN', name: 'Barcelona El Prat', city: 'Barcelona', country: 'Spain', lat: 41.2971, lon: 2.0785 },
  { code: 'PMI', name: 'Palma de Mallorca', city: 'Palma', country: 'Spain', lat: 39.5517, lon: 2.7388 },
  { code: 'AGP', name: 'Málaga Costa del Sol', city: 'Málaga', country: 'Spain', lat: 36.6749, lon: -4.4991 },
  { code: 'ALC', name: 'Alicante Elche', city: 'Alicante', country: 'Spain', lat: 38.2822, lon: -0.5582 },
  { code: 'VLC', name: 'Valencia', city: 'Valencia', country: 'Spain', lat: 39.4893, lon: -0.4816 },
  { code: 'SVQ', name: 'Seville San Pablo', city: 'Seville', country: 'Spain', lat: 37.4180, lon: -5.8931 },
  { code: 'BIO', name: 'Bilbao', city: 'Bilbao', country: 'Spain', lat: 43.3011, lon: -2.9106 },
  { code: 'TFS', name: 'Tenerife South', city: 'Tenerife', country: 'Spain', lat: 28.0445, lon: -16.5725 },
  { code: 'LPA', name: 'Gran Canaria', city: 'Las Palmas', country: 'Spain', lat: 27.9319, lon: -15.3866 },
  // Italy
  { code: 'FCO', name: 'Rome Fiumicino', city: 'Rome', country: 'Italy', lat: 41.8003, lon: 12.2389 },
  { code: 'CIA', name: 'Rome Ciampino', city: 'Rome', country: 'Italy', lat: 41.7994, lon: 12.5949 },
  { code: 'MXP', name: 'Milan Malpensa', city: 'Milan', country: 'Italy', lat: 45.6306, lon: 8.7281 },
  { code: 'LIN', name: 'Milan Linate', city: 'Milan', country: 'Italy', lat: 45.4454, lon: 9.2767 },
  { code: 'BGY', name: 'Milan Bergamo', city: 'Bergamo', country: 'Italy', lat: 45.6739, lon: 9.7042 },
  { code: 'VCE', name: 'Venice Marco Polo', city: 'Venice', country: 'Italy', lat: 45.5053, lon: 12.3519 },
  { code: 'NAP', name: 'Naples Capodichino', city: 'Naples', country: 'Italy', lat: 40.8860, lon: 14.2908 },
  { code: 'BLQ', name: 'Bologna Marconi', city: 'Bologna', country: 'Italy', lat: 44.5354, lon: 11.2887 },
  { code: 'PSA', name: 'Pisa Galileo Galilei', city: 'Pisa', country: 'Italy', lat: 43.6839, lon: 10.3927 },
  { code: 'FLR', name: 'Florence Peretola', city: 'Florence', country: 'Italy', lat: 43.8100, lon: 11.2051 },
  { code: 'TRN', name: 'Turin Caselle', city: 'Turin', country: 'Italy', lat: 45.2008, lon: 7.6497 },
  { code: 'CTA', name: 'Catania Fontanarossa', city: 'Catania', country: 'Italy', lat: 37.4668, lon: 15.0664 },
  { code: 'PMO', name: 'Palermo Falcone-Borsellino', city: 'Palermo', country: 'Italy', lat: 38.1760, lon: 13.0910 },
  // Eastern Europe
  { code: 'PRG', name: 'Prague Václav Havel', city: 'Prague', country: 'Czech Republic', lat: 50.1008, lon: 14.2600 },
  { code: 'VIE', name: 'Vienna', city: 'Vienna', country: 'Austria', lat: 48.1103, lon: 16.5697 },
  { code: 'BUD', name: 'Budapest', city: 'Budapest', country: 'Hungary', lat: 47.4298, lon: 19.2611 },
  { code: 'OTP', name: 'Bucharest Otopeni', city: 'Bucharest', country: 'Romania', lat: 44.5711, lon: 26.0850 },
  { code: 'SOF', name: 'Sofia', city: 'Sofia', country: 'Bulgaria', lat: 42.6952, lon: 23.4062 },
  { code: 'ZAG', name: 'Zagreb', city: 'Zagreb', country: 'Croatia', lat: 45.7429, lon: 16.0688 },
  { code: 'LJU', name: 'Ljubljana Jože Pučnik', city: 'Ljubljana', country: 'Slovenia', lat: 46.2237, lon: 14.4576 },
  // Greece
  { code: 'ATH', name: 'Athens Eleftherios Venizelos', city: 'Athens', country: 'Greece', lat: 37.9364, lon: 23.9445 },
  { code: 'SKG', name: 'Thessaloniki Macedonia', city: 'Thessaloniki', country: 'Greece', lat: 40.5197, lon: 22.9709 },
  // Portugal
  { code: 'LIS', name: 'Lisbon Humberto Delgado', city: 'Lisbon', country: 'Portugal', lat: 38.7813, lon: -9.1359 },
  { code: 'OPO', name: 'Porto Francisco Sá Carneiro', city: 'Porto', country: 'Portugal', lat: 41.2481, lon: -8.6814 },
  { code: 'FAO', name: 'Faro', city: 'Faro', country: 'Portugal', lat: 37.0144, lon: -7.9659 },
  // Ireland
  { code: 'DUB', name: 'Dublin', city: 'Dublin', country: 'Ireland', lat: 53.4264, lon: -6.2499 },
  // Switzerland
  { code: 'ZRH', name: 'Zürich', city: 'Zürich', country: 'Switzerland', lat: 47.4647, lon: 8.5492 },
  { code: 'GVA', name: 'Geneva', city: 'Geneva', country: 'Switzerland', lat: 46.2381, lon: 6.1089 },
  { code: 'BSL', name: 'Basel Mulhouse', city: 'Basel', country: 'Switzerland', lat: 47.5896, lon: 7.5299 },
];

// Custom marker icons
const createPersonIcon = (color = '#3b82f6', isDriver = false, isPassenger = false, isSelected = false) => {
  let emoji = '👤';
  let size = 28;
  let borderWidth = 3;

  if (isDriver) {
    emoji = '🚗';
    size = 32;
    borderWidth = 4;
  } else if (isPassenger) {
    emoji = '🧑';
    size = 26;
  }

  const borderColor = isSelected ? '#fbbf24' : 'white';
  const extraBorder = isSelected ? 'outline: 3px solid #fbbf24; outline-offset: 2px;' : '';

  return new L.DivIcon({
    html: `
      <div style="
        background-color: ${color};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: ${borderWidth}px solid ${borderColor};
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: ${isDriver ? 16 : 14}px;
        ${extraBorder}
        cursor: pointer;
      ">${emoji}</div>
    `,
    className: 'custom-person-marker',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2]
  });
};

const createProjectIcon = () => {
  return new L.DivIcon({
    html: `
      <div style="
        background-color: #22c55e;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 4px solid white;
        box-shadow: 0 3px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 18px;
      ">🏗️</div>
    `,
    className: 'custom-project-marker',
    iconSize: [36, 36],
    iconAnchor: [18, 18]
  });
};

// Airport marker icon
const createAirportIcon = (isDestination = false) => {
  const bgColor = isDestination ? '#dc2626' : '#8b5cf6'; // Red for destination, purple for origin
  return new L.DivIcon({
    html: `
      <div style="
        background-color: ${bgColor};
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 3px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 16px;
      ">✈️</div>
    `,
    className: 'custom-airport-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 16]
  });
};

// Find nearest airport to a location
const findNearestAirport = (lat, lon) => {
  if (!lat || !lon) return null;

  let nearest = null;
  let minDistance = Infinity;

  AIRPORTS.forEach(airport => {
    const R = 6371;
    const dLat = (airport.lat - lat) * Math.PI / 180;
    const dLon = (airport.lon - lon) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat * Math.PI / 180) * Math.cos(airport.lat * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;

    if (distance < minDistance) {
      minDistance = distance;
      nearest = { ...airport, distance };
    }
  });

  return nearest;
};

// Generate curved flight path points for visualization
const generateFlightPath = (startLat, startLon, endLat, endLon, numPoints = 50) => {
  const points = [];

  // Calculate great circle path with curve
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;

    // Linear interpolation for basic path
    const lat = startLat + t * (endLat - startLat);
    const lon = startLon + t * (endLon - startLon);

    // Add curve (parabolic arc to simulate flight path)
    const curveHeight = Math.sin(t * Math.PI) * 2; // Max 2 degrees curve
    const curveLat = lat + curveHeight;

    points.push([curveLat, lon]);
  }

  return points;
};

// Component to fit map bounds
const FitBounds = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.length > 0) {
      const validBounds = bounds.filter(b => b && b[0] && b[1]);
      if (validBounds.length > 0) {
        map.fitBounds(validBounds, { padding: [50, 50], maxZoom: 10 });
      }
    }
  }, [bounds, map]);

  return null;
};

// ============ OPTIMIZED GEOCODING SYSTEM ============

// Load cache from localStorage
const CACHE_KEY = 'winduno_geocode_cache';
const loadCache = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
};

const saveCache = (cache) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // localStorage full or unavailable
  }
};

// Persistent geocoding cache
let geocodeCache = loadCache();

// Extract city from Polish address
const extractCity = (address) => {
  if (!address) return null;

  // Format: "City, Street, PostalCode" or "City,"
  const cityMatch = address.match(/^([^,]+)/);
  if (cityMatch) {
    return cityMatch[1].trim();
  }
  return null;
};

// Single geocode request (no retries, fast) - uses backend proxy to avoid CORS
const geocodeSingle = async (query, countryCode = null) => {
  try {
    // Use backend proxy instead of direct Nominatim call (CORS blocked)
    const params = new URLSearchParams({
      q: query,
      limit: '1'
    });
    if (countryCode) {
      params.append('countrycodes', countryCode);
    }

    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE_URL}/geocode/?${params}`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.status}`);
    }

    const data = await response.json();

    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lon: parseFloat(data[0].lon),
        displayName: data[0].display_name
      };
    }
  } catch (error) {
    console.error('Geocoding error:', error);
  }
  return null;
};

// Geocode address - optimized with city fallback
const geocodeAddress = async (address) => {
  if (!address) return null;

  // Check cache first (instant)
  if (geocodeCache[address]) {
    return geocodeCache[address];
  }

  // Try city only first (faster, more reliable)
  const city = extractCity(address);
  if (city) {
    const result = await geocodeSingle(`${city}, Poland`, 'pl');
    if (result) {
      geocodeCache[address] = result;
      saveCache(geocodeCache);
      return result;
    }
  }

  // Fallback: try full address
  const result = await geocodeSingle(`${address}, Poland`, 'pl');
  if (result) {
    geocodeCache[address] = result;
    saveCache(geocodeCache);
    return result;
  }

  // Mark as not found in cache
  geocodeCache[address] = null;
  saveCache(geocodeCache);
  return null;
};

// Geocode international address (for projects in any country)
const geocodeInternationalAddress = async (address, country = null) => {
  if (!address) return null;

  const cacheKey = `intl_${address}_${country || ''}`;

  // Check cache first (instant)
  if (geocodeCache[cacheKey]) {
    return geocodeCache[cacheKey];
  }

  const query = country ? `${address}, ${country}` : address;
  const result = await geocodeSingle(query);

  if (result) {
    geocodeCache[cacheKey] = result;
    saveCache(geocodeCache);
    return result;
  }

  // Mark as not found
  geocodeCache[cacheKey] = null;
  saveCache(geocodeCache);
  return null;
};

// Batch geocode multiple addresses in parallel (max 5 concurrent)
const batchGeocode = async (addresses, geocodeFn) => {
  const results = {};
  const BATCH_SIZE = 5;

  // Filter out already cached addresses
  const toGeocode = addresses.filter(addr => {
    if (geocodeCache[addr]) {
      results[addr] = geocodeCache[addr];
      return false;
    }
    return true;
  });

  // Process in batches
  for (let i = 0; i < toGeocode.length; i += BATCH_SIZE) {
    const batch = toGeocode.slice(i, i + BATCH_SIZE);
    const batchResults = await Promise.all(
      batch.map(addr => geocodeFn(addr))
    );

    batch.forEach((addr, idx) => {
      results[addr] = batchResults[idx];
    });

    // Small delay between batches to respect rate limits
    if (i + BATCH_SIZE < toGeocode.length) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }

  return results;
};

const TravelMapPanel = ({
  selectedPeople = [],
  allPeople = [],
  project = null,
  projects = [],              // Multiple projects for full map view
  getUserName,
  transportType = 'car',
  usersData = {},
  departureTime = '',
  departureAirport = '',      // Passed from parent
  destinationAirport = '',    // Passed from parent
  isFullMapView = false,      // Flag for full map view mode
  airportCodes = [],          // Airport codes to display on map (for full map view)
  // Pickup mode props
  pickupMode = false,         // Whether pickup mode is active
  selectedDriverId = null,    // Currently selected driver travel ID
  onSelectDriver = null,      // Callback when driver is selected
  onSetPickupDriver = null,   // Callback to set pickup relationship (passengerId, driverId)
  onClearPickupDriver = null, // Callback to clear pickup relationship
  // Organized transport props
  organizedTransport = false, // Whether organized transport is enabled
  pickupPoint = '',           // Pickup location (e.g., airport terminal)
  dropoffLocation = '',       // Final dropoff location
  travelType = null           // 'check_in' or 'check_out'
}) => {
  const [personLocations, setPersonLocations] = useState({});
  const [projectLocation, setProjectLocation] = useState(null);
  const [projectLocations, setProjectLocations] = useState([]); // Multiple project locations
  const [loading, setLoading] = useState(true);

  // Organized transport locations
  const [pickupLocation, setPickupLocation] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [homeLocation, setHomeLocation] = useState(null);
  const [geocodingStatus, setGeocodingStatus] = useState({});
  const [customRoute, setCustomRoute] = useState([]); // Custom route order
  const [useCustomRoute, setUseCustomRoute] = useState(false);

  // Toggle person in custom route
  const togglePersonInRoute = (travelId) => {
    setCustomRoute(prev => {
      if (prev.includes(travelId)) {
        // Remove from route
        return prev.filter(id => id !== travelId);
      } else {
        // Add to route
        return [...prev, travelId];
      }
    });
    setUseCustomRoute(true);
  };

  // Clear custom route
  const clearCustomRoute = () => {
    setCustomRoute([]);
    setUseCustomRoute(false);
  };

  // Move person up in route
  const moveUp = (travelId) => {
    setCustomRoute(prev => {
      const idx = prev.indexOf(travelId);
      if (idx > 0) {
        const newRoute = [...prev];
        [newRoute[idx - 1], newRoute[idx]] = [newRoute[idx], newRoute[idx - 1]];
        return newRoute;
      }
      return prev;
    });
  };

  // Move person down in route
  const moveDown = (travelId) => {
    setCustomRoute(prev => {
      const idx = prev.indexOf(travelId);
      if (idx < prev.length - 1) {
        const newRoute = [...prev];
        [newRoute[idx], newRoute[idx + 1]] = [newRoute[idx + 1], newRoute[idx]];
        return newRoute;
      }
      return prev;
    });
  };

  // Get selected travel data
  const selectedTravels = useMemo(() => {
    return selectedPeople
      .map(id => allPeople.find(t => t.id === id))
      .filter(Boolean);
  }, [selectedPeople, allPeople]);

  // Geocode project address (single project)
  useEffect(() => {
    const geocodeProject = async () => {
      if (!project) {
        setProjectLocation(null);
        return;
      }

      // Build full address with postal code
      const addressParts = [
        project.street_address,
        project.postal_code,
        project.city
      ].filter(Boolean).join(', ');

      const fullAddress = [addressParts, project.country].filter(Boolean).join(', ');

      console.log(`Geocoding project "${project.name}": ${fullAddress}`);

      if (fullAddress) {
        // Use international geocoding (no country restriction)
        const coords = await geocodeInternationalAddress(addressParts, project.country);
        if (coords) {
          setProjectLocation({
            ...coords,
            name: project.name,
            address: fullAddress
          });
        } else {
          console.warn(`Could not geocode project: ${fullAddress}`);
        }
      }
    };

    geocodeProject();
  }, [project]);

  // Create stable key for projects array to prevent infinite loops
  const projectsKey = useMemo(() => {
    if (!projects || projects.length === 0) return '';
    return projects.map(p => p.id).sort().join(',');
  }, [projects]);

  // Geocode multiple projects (for full map view)
  useEffect(() => {
    const geocodeProjects = async () => {
      if (!isFullMapView || !projects || projects.length === 0) {
        setProjectLocations([]);
        return;
      }

      const locations = [];

      for (const proj of projects) {
        const addressParts = [
          proj.street_address,
          proj.postal_code,
          proj.city
        ].filter(Boolean).join(', ');

        const fullAddress = [addressParts, proj.country].filter(Boolean).join(', ');

        if (fullAddress) {
          const coords = await geocodeInternationalAddress(addressParts, proj.country);
          if (coords) {
            locations.push({
              ...coords,
              id: proj.id,
              name: proj.name,
              address: fullAddress,
              city: proj.city,
              country: proj.country
            });
          }
        }
      }

      setProjectLocations(locations);
    };

    geocodeProjects();
  }, [isFullMapView, projectsKey]); // Use stable key instead of projects array

  // Geocode person addresses - optimized batch processing
  useEffect(() => {
    const geocodePeople = async () => {
      // Wait for users data to be loaded
      if (Object.keys(usersData).length === 0) {
        return;
      }

      setLoading(true);
      const newLocations = {};
      const newStatus = {};

      // Collect all unique addresses to geocode
      const addressMap = {}; // address -> [travel objects]
      selectedTravels.forEach(travel => {
        const user = usersData[travel.user_id] || {};
        const address = user.personal_address;

        if (address) {
          if (!addressMap[address]) {
            addressMap[address] = [];
          }
          addressMap[address].push({ travel, user });
          newStatus[travel.id] = 'geocoding';
        } else {
          newStatus[travel.id] = 'no_address';
        }
      });

      // Batch geocode all unique addresses
      const addresses = Object.keys(addressMap);
      const geocodeResults = await batchGeocode(addresses, geocodeAddress);

      // Map results back to travels
      Object.entries(addressMap).forEach(([address, items]) => {
        const coords = geocodeResults[address];
        items.forEach(({ travel, user }) => {
          if (coords) {
            newLocations[travel.id] = {
              ...coords,
              name: getUserName ? getUserName(travel) : `${user.first_name} ${user.last_name}`,
              address: address,
              userId: travel.user_id
            };
            newStatus[travel.id] = 'found';
          } else {
            newStatus[travel.id] = 'not_found';
          }
        });
      });

      setPersonLocations(newLocations);
      setGeocodingStatus(newStatus);
      setLoading(false);
    };

    if (selectedTravels.length > 0 && Object.keys(usersData).length > 0) {
      geocodePeople();
    } else if (selectedTravels.length === 0) {
      setLoading(false);
    }
  }, [selectedTravels, getUserName, usersData]);

  // Geocode organized transport locations (pickup, dropoff, home)
  useEffect(() => {
    const geocodeTransportLocations = async () => {
      // Only when organized transport is enabled
      if (!organizedTransport || transportType !== 'plane') {
        setPickupLocation(null);
        setDropoffCoords(null);
        setHomeLocation(null);
        return;
      }

      // Geocode pickup point
      if (pickupPoint) {
        const pickupCoords = await geocodeInternationalAddress(pickupPoint);
        if (pickupCoords) {
          setPickupLocation({ ...pickupCoords, name: pickupPoint });
        }
      } else {
        setPickupLocation(null);
      }

      // Geocode dropoff location
      if (dropoffLocation) {
        const dropCoords = await geocodeInternationalAddress(dropoffLocation);
        if (dropCoords) {
          setDropoffCoords({ ...dropCoords, name: dropoffLocation });
        }
      } else {
        setDropoffCoords(null);
      }

      // Geocode home address for comparison (if dropoff is different from home)
      if (selectedTravels.length > 0) {
        const firstTravel = selectedTravels[0];
        const homeAddress = usersData[firstTravel.user_id]?.personal_address;
        if (homeAddress && dropoffLocation && homeAddress.trim().toLowerCase() !== dropoffLocation.trim().toLowerCase()) {
          const homeCoords = await geocodeAddress(homeAddress);
          if (homeCoords) {
            setHomeLocation({ ...homeCoords, name: homeAddress });
          }
        } else {
          setHomeLocation(null);
        }
      }
    };

    geocodeTransportLocations();
  }, [organizedTransport, transportType, pickupPoint, dropoffLocation, selectedTravels, usersData]);

  // Get airport data from codes (for full map view)
  const airportsToShow = useMemo(() => {
    if (!isFullMapView || !airportCodes || airportCodes.length === 0) return [];
    return airportCodes
      .map(code => AIRPORTS.find(a => a.code === code))
      .filter(Boolean);
  }, [isFullMapView, airportCodes]);

  // Calculate map bounds
  const mapBounds = useMemo(() => {
    const bounds = [];

    // Add project location (single)
    if (projectLocation) {
      bounds.push([projectLocation.lat, projectLocation.lon]);
    }

    // Add multiple project locations (for full map view)
    if (isFullMapView && projectLocations.length > 0) {
      projectLocations.forEach(loc => {
        if (loc && loc.lat && loc.lon) {
          bounds.push([loc.lat, loc.lon]);
        }
      });
    }

    // Add airport locations (for full map view)
    if (isFullMapView && airportsToShow.length > 0) {
      airportsToShow.forEach(airport => {
        bounds.push([airport.lat, airport.lon]);
      });
    }

    // Add person locations
    Object.values(personLocations).forEach(loc => {
      if (loc && loc.lat && loc.lon) {
        bounds.push([loc.lat, loc.lon]);
      }
    });

    // Add organized transport locations
    if (pickupLocation) {
      bounds.push([pickupLocation.lat, pickupLocation.lon]);
    }
    if (dropoffCoords) {
      bounds.push([dropoffCoords.lat, dropoffCoords.lon]);
    }
    if (homeLocation) {
      bounds.push([homeLocation.lat, homeLocation.lon]);
    }

    return bounds;
  }, [projectLocation, projectLocations, personLocations, isFullMapView, airportsToShow, pickupLocation, dropoffCoords, homeLocation]);

  // Default center (Poland)
  const defaultCenter = [52.0, 19.0];
  const defaultZoom = 6;

  // Calculate distances
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Get person colors based on distance grouping
  const getPersonColor = (personId) => {
    const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6'];
    const index = Object.keys(personLocations).indexOf(personId.toString()) % colors.length;
    return colors[index];
  };

  // Calculate airport info for plane transport mode
  const airportInfo = useMemo(() => {
    if (transportType !== 'plane') return null;

    // Use destination airport from props
    let destAirportData = null;
    if (destinationAirport) {
      const airport = AIRPORTS.find(a => a.code === destinationAirport);
      if (airport) {
        destAirportData = {
          ...airport,
          distance: projectLocation
            ? Math.sqrt(
                Math.pow((airport.lat - projectLocation.lat) * 111, 2) +
                Math.pow((airport.lon - projectLocation.lon) * 111 * Math.cos(projectLocation.lat * Math.PI / 180), 2)
              )
            : 0
        };
      }
    }

    // Use departure airport from props
    let depAirportData = null;
    if (departureAirport) {
      depAirportData = AIRPORTS.find(a => a.code === departureAirport);
    }

    // Calculate distances from each person to departure airport
    const personDistancesToAirport = {};
    if (depAirportData) {
      Object.entries(personLocations).forEach(([travelId, loc]) => {
        const distance = Math.sqrt(
          Math.pow((loc.lat - depAirportData.lat) * 111, 2) +
          Math.pow((loc.lon - depAirportData.lon) * 111 * Math.cos(loc.lat * Math.PI / 180), 2)
        );
        personDistancesToAirport[travelId] = distance;
      });
    }

    // All people depart from the same selected airport
    const airportGroups = {};
    if (depAirportData) {
      airportGroups[depAirportData.code] = {
        airport: depAirportData,
        people: selectedTravels.map(travel => ({
          travelId: travel.id,
          travel,
          name: getUserName ? getUserName(travel) : `User ${travel.user_id}`,
          distanceToAirport: personDistancesToAirport[travel.id] || null
        }))
      };
    }

    // Calculate flight distance
    let flightDistance = null;
    if (depAirportData && destAirportData) {
      const R = 6371;
      const dLat = (destAirportData.lat - depAirportData.lat) * Math.PI / 180;
      const dLon = (destAirportData.lon - depAirportData.lon) * Math.PI / 180;
      const a =
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(depAirportData.lat * Math.PI / 180) * Math.cos(destAirportData.lat * Math.PI / 180) *
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      flightDistance = R * c;
    }

    return {
      destinationAirport: destAirportData,
      departureAirport: depAirportData,
      airportGroups,
      personDistancesToAirport,
      flightDistance
    };
  }, [transportType, destinationAirport, departureAirport, projectLocation, personLocations, selectedTravels, getUserName]);

  // Extended map bounds for plane mode (include airports)
  const extendedMapBounds = useMemo(() => {
    if (transportType !== 'plane' || !airportInfo) return mapBounds;

    const bounds = [...mapBounds];

    // Add destination airport
    if (airportInfo.destinationAirport) {
      bounds.push([airportInfo.destinationAirport.lat, airportInfo.destinationAirport.lon]);
    }

    // Add departure airport
    if (airportInfo.departureAirport) {
      bounds.push([airportInfo.departureAirport.lat, airportInfo.departureAirport.lon]);
    }

    return bounds;
  }, [transportType, airportInfo, mapBounds]);

  const locatedPeople = Object.keys(personLocations).length;
  const totalPeople = selectedTravels.length;

  return (
    <div className="h-full flex flex-col">
      {/* Header - hidden in full map view */}
      {!isFullMapView && (
        <div className="p-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-semibold text-gray-900 flex items-center">
              <MapPin className="h-4 w-4 mr-1 text-green-600" />
              Route Map
            </h4>
            {project && (
              <div className="text-xs text-right">
                <span className="font-medium text-gray-800">🏗️ {project.name}</span>
                <span className="text-gray-500 ml-1">
                  ({[project.city, project.country].filter(Boolean).join(', ')})
                </span>
              </div>
            )}
          </div>

          {/* Status indicators */}
          <div className="flex flex-wrap gap-1 text-xs">
            <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded">
              <Users className="h-3 w-3" />
              <span>Located: {locatedPeople}/{totalPeople}</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded">
              {transportType === 'car' ? <Car className="h-3 w-3" /> : <Plane className="h-3 w-3" />}
              <span>{transportType === 'car' ? 'Car' : 'Plane'}</span>
            </div>
            {/* Show GPS status only for check_in or car mode */}
            {(travelType === 'check_in' || transportType === 'car') && projectLocation && (
              <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded">
                <Navigation className="h-3 w-3" />
                <span>GPS: ✓</span>
              </div>
            )}
            {(travelType === 'check_in' || transportType === 'car') && project && !projectLocation && (
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded">
                <AlertCircle className="h-3 w-3" />
                <span>Locating...</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Map */}
      <div className="flex-1 relative" style={{ minHeight: '250px' }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Locating addresses...</p>
            </div>
          </div>
        ) : (
          <MapContainer
            center={mapBounds.length > 0 ? mapBounds[0] : defaultCenter}
            zoom={defaultZoom}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <FitBounds bounds={transportType === 'plane' ? extendedMapBounds : mapBounds} />

            {/* Project marker (single) - hide for check_out with plane */}
            {projectLocation && !isFullMapView && (travelType === 'check_in' || transportType === 'car') && (
              <Marker
                position={[projectLocation.lat, projectLocation.lon]}
                icon={createProjectIcon()}
              >
                <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                  <span className="font-medium">🏗️ {projectLocation.name}</span>
                </Tooltip>
                <Popup>
                  <div className="p-1">
                    <h3 className="font-bold text-green-700 mb-1">🏗️ {projectLocation.name}</h3>
                    <p className="text-xs text-gray-600">{projectLocation.address}</p>
                    {transportType === 'plane' && airportInfo?.destinationAirport && (
                      <p className="text-xs text-purple-600 mt-1">
                        ✈️ Nearest airport: {airportInfo.destinationAirport.name} ({airportInfo.destinationAirport.code})
                        <br />📏 {airportInfo.destinationAirport.distance.toFixed(0)} km away
                      </p>
                    )}
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Multiple project markers (for full map view) */}
            {isFullMapView && projectLocations.map(projLoc => (
              <Marker
                key={projLoc.id}
                position={[projLoc.lat, projLoc.lon]}
                icon={createProjectIcon()}
              >
                <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                  <span className="font-medium">🏗️ {projLoc.name}</span>
                </Tooltip>
                <Popup>
                  <div className="p-1">
                    <h3 className="font-bold text-green-700 mb-1">🏗️ {projLoc.name}</h3>
                    <p className="text-xs text-gray-600">{projLoc.address}</p>
                    <p className="text-xs text-gray-500">{projLoc.city}, {projLoc.country}</p>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Airport markers for full map view */}
            {isFullMapView && airportsToShow.map(airport => (
              <Marker
                key={airport.code}
                position={[airport.lat, airport.lon]}
                icon={createAirportIcon(false)}
              >
                <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                  <span className="font-medium">✈️ {airport.code} - {airport.city}</span>
                </Tooltip>
                <Popup>
                  <div className="p-1">
                    <h3 className="font-bold text-purple-700 mb-1">
                      ✈️ {airport.name}
                    </h3>
                    <p className="text-xs text-gray-600">
                      Code: <strong>{airport.code}</strong>
                    </p>
                    <p className="text-xs text-gray-500">
                      {airport.city}, {airport.country}
                    </p>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Airport markers and flight paths for plane mode */}
            {transportType === 'plane' && airportInfo && (
              <>
                {/* Destination airport marker */}
                {airportInfo.destinationAirport && (
                  <Marker
                    position={[airportInfo.destinationAirport.lat, airportInfo.destinationAirport.lon]}
                    icon={createAirportIcon(true)}
                  >
                    <Popup>
                      <div className="p-1">
                        <h3 className="font-bold text-red-700 mb-1">
                          🛬 {airportInfo.destinationAirport.name}
                        </h3>
                        <p className="text-xs text-gray-600">
                          Code: <strong>{airportInfo.destinationAirport.code}</strong>
                        </p>
                        <p className="text-xs text-gray-500">
                          {airportInfo.destinationAirport.city}, {airportInfo.destinationAirport.country}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          📍 Destination airport - {airportInfo.destinationAirport.distance?.toFixed(0)} km from project
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                )}

                {/* Departure airport marker */}
                {airportInfo.departureAirport && (
                  <Marker
                    position={[airportInfo.departureAirport.lat, airportInfo.departureAirport.lon]}
                    icon={createAirportIcon(false)}
                  >
                    <Popup>
                      <div className="p-1">
                        <h3 className="font-bold text-purple-700 mb-1">
                          🛫 {airportInfo.departureAirport.name}
                        </h3>
                        <p className="text-xs text-gray-600">
                          Code: <strong>{airportInfo.departureAirport.code}</strong>
                        </p>
                        <p className="text-xs text-gray-500">
                          {airportInfo.departureAirport.city}, {airportInfo.departureAirport.country}
                        </p>
                        <div className="text-xs text-blue-600 mt-1">
                          👥 {selectedTravels.length} passenger{selectedTravels.length !== 1 ? 's' : ''} departing
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                )}

                {/* Flight path from departure to destination airport */}
                {airportInfo.departureAirport && airportInfo.destinationAirport && (
                  <Polyline
                    positions={generateFlightPath(
                      airportInfo.departureAirport.lat,
                      airportInfo.departureAirport.lon,
                      airportInfo.destinationAirport.lat,
                      airportInfo.destinationAirport.lon
                    )}
                    color="#8b5cf6"
                    weight={3}
                    opacity={0.7}
                    dashArray="10, 5"
                  />
                )}

                {/* Lines from people to departure airport - only for check_in without organized transport */}
                {travelType === 'check_in' && !organizedTransport && airportInfo.departureAirport && Object.entries(personLocations).map(([travelId, location]) => (
                  <Polyline
                    key={`to-airport-${travelId}`}
                    positions={[
                      [location.lat, location.lon],
                      [airportInfo.departureAirport.lat, airportInfo.departureAirport.lon]
                    ]}
                    color="#a855f7"
                    weight={2}
                    opacity={0.5}
                    dashArray="5, 10"
                  />
                ))}

                {/* Lines from people to pickup point - only for check_in with organized transport */}
                {travelType === 'check_in' && organizedTransport && pickupLocation && Object.entries(personLocations).map(([travelId, location]) => (
                  <Polyline
                    key={`to-pickup-${travelId}`}
                    positions={[
                      [location.lat, location.lon],
                      [pickupLocation.lat, pickupLocation.lon]
                    ]}
                    color="#a855f7"
                    weight={2}
                    opacity={0.5}
                    dashArray="5, 10"
                  />
                ))}

                {/* Line from destination airport to project - only for check_in and no organized transport */}
                {travelType === 'check_in' && airportInfo.destinationAirport && projectLocation && !organizedTransport && (
                  <Polyline
                    positions={[
                      [airportInfo.destinationAirport.lat, airportInfo.destinationAirport.lon],
                      [projectLocation.lat, projectLocation.lon]
                    ]}
                    color="#22c55e"
                    weight={3}
                    opacity={0.7}
                    dashArray="8, 8"
                  />
                )}

                {/* Organized Transport Route - different for check_in vs check_out */}
                {organizedTransport && (
                  <>
                    {/* Pickup point marker - always show when we have location */}
                    {pickupLocation && (
                      <Marker
                        position={[pickupLocation.lat, pickupLocation.lon]}
                        icon={new L.DivIcon({
                          html: `<div style="background-color: #f59e0b; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 3px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; font-size: 16px;">📍</div>`,
                          className: 'custom-pickup-marker',
                          iconSize: [32, 32],
                          iconAnchor: [16, 16]
                        })}
                      >
                        <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                          <span className="font-medium">📍 {travelType === 'check_in' ? 'Meeting Point' : 'Pickup'}: {pickupLocation.name}</span>
                        </Tooltip>
                        <Popup>
                          <div className="p-1">
                            <h3 className="font-bold text-amber-700 mb-1">📍 {travelType === 'check_in' ? 'Meeting Point' : 'Pickup Point'}</h3>
                            <p className="text-xs text-gray-600">{pickupLocation.name}</p>
                            {travelType === 'check_in' && (
                              <p className="text-xs text-amber-600 mt-1">Everyone gathers here before transport to airport</p>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    )}

                    {/* Dropoff location marker */}
                    {dropoffCoords && (
                      <Marker
                        position={[dropoffCoords.lat, dropoffCoords.lon]}
                        icon={new L.DivIcon({
                          html: `<div style="background-color: #10b981; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 3px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; font-size: 16px;">🏁</div>`,
                          className: 'custom-dropoff-marker',
                          iconSize: [32, 32],
                          iconAnchor: [16, 16]
                        })}
                      >
                        <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                          <span className="font-medium">🏁 Dropoff: {dropoffCoords.name}</span>
                        </Tooltip>
                        <Popup>
                          <div className="p-1">
                            <h3 className="font-bold text-emerald-700 mb-1">🏁 Dropoff Location</h3>
                            <p className="text-xs text-gray-600">{dropoffCoords.name}</p>
                          </div>
                        </Popup>
                      </Marker>
                    )}

                    {/* Home location marker (for check_out) */}
                    {travelType === 'check_out' && homeLocation && (
                      <Marker
                        position={[homeLocation.lat, homeLocation.lon]}
                        icon={new L.DivIcon({
                          html: `<div style="background-color: #3b82f6; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 3px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; font-size: 16px;">🏠</div>`,
                          className: 'custom-home-marker',
                          iconSize: [32, 32],
                          iconAnchor: [16, 16]
                        })}
                      >
                        <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                          <span className="font-medium">🏠 Home</span>
                        </Tooltip>
                        <Popup>
                          <div className="p-1">
                            <h3 className="font-bold text-blue-700 mb-1">🏠 Home Address</h3>
                            <p className="text-xs text-gray-600">{homeLocation.name}</p>
                          </div>
                        </Popup>
                      </Marker>
                    )}

                    {/* CHECK_IN ROUTES: Pickup → Departure Airport, then Destination Airport → Project */}
                    {travelType === 'check_in' && (
                      <>
                        {/* Route: Meeting Point → Departure Airport */}
                        {pickupLocation && airportInfo.departureAirport && (
                          <Polyline
                            positions={[
                              [pickupLocation.lat, pickupLocation.lon],
                              [airportInfo.departureAirport.lat, airportInfo.departureAirport.lon]
                            ]}
                            color="#f59e0b"
                            weight={4}
                            opacity={0.8}
                          />
                        )}

                        {/* Route: Destination Airport → Dropoff (or Project) */}
                        {airportInfo.destinationAirport && dropoffCoords && (
                          <Polyline
                            positions={[
                              [airportInfo.destinationAirport.lat, airportInfo.destinationAirport.lon],
                              [dropoffCoords.lat, dropoffCoords.lon]
                            ]}
                            color="#10b981"
                            weight={4}
                            opacity={0.8}
                          />
                        )}

                        {/* Route: Destination Airport → Project (if no dropoff specified) */}
                        {airportInfo.destinationAirport && !dropoffCoords && projectLocation && (
                          <Polyline
                            positions={[
                              [airportInfo.destinationAirport.lat, airportInfo.destinationAirport.lon],
                              [projectLocation.lat, projectLocation.lon]
                            ]}
                            color="#22c55e"
                            weight={4}
                            opacity={0.8}
                          />
                        )}
                      </>
                    )}

                    {/* CHECK_OUT ROUTES: Destination Airport → Pickup → Dropoff → Home */}
                    {travelType === 'check_out' && airportInfo.destinationAirport && (
                      <>
                        {/* Route: Destination Airport → Pickup */}
                        {pickupLocation && (
                          <Polyline
                            positions={[
                              [airportInfo.destinationAirport.lat, airportInfo.destinationAirport.lon],
                              [pickupLocation.lat, pickupLocation.lon]
                            ]}
                            color="#f59e0b"
                            weight={4}
                            opacity={0.8}
                          />
                        )}

                        {/* Route: Pickup → Dropoff */}
                        {pickupLocation && dropoffCoords && (
                          <Polyline
                            positions={[
                              [pickupLocation.lat, pickupLocation.lon],
                              [dropoffCoords.lat, dropoffCoords.lon]
                            ]}
                            color="#10b981"
                            weight={4}
                            opacity={0.8}
                          />
                        )}

                        {/* Route: Airport → Dropoff (if no pickup) */}
                        {!pickupLocation && dropoffCoords && (
                          <Polyline
                            positions={[
                              [airportInfo.destinationAirport.lat, airportInfo.destinationAirport.lon],
                              [dropoffCoords.lat, dropoffCoords.lon]
                            ]}
                            color="#10b981"
                            weight={4}
                            opacity={0.8}
                          />
                        )}

                        {/* Route: Dropoff → Home (if different) */}
                        {dropoffCoords && homeLocation && (
                          <Polyline
                            positions={[
                              [dropoffCoords.lat, dropoffCoords.lon],
                              [homeLocation.lat, homeLocation.lon]
                            ]}
                            color="#3b82f6"
                            weight={3}
                            opacity={0.7}
                            dashArray="8, 8"
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {/* Person markers */}
            {(() => {
              // Calculate offset for overlapping markers
              const locationCounts = {};
              const locationIndices = {};

              Object.entries(personLocations).forEach(([travelId, loc]) => {
                const key = `${loc.lat.toFixed(4)}_${loc.lon.toFixed(4)}`;
                if (!locationCounts[key]) {
                  locationCounts[key] = 0;
                  locationIndices[key] = 0;
                }
                locationCounts[key]++;
              });

              return Object.entries(personLocations).map(([travelId, location]) => {
                const travel = selectedTravels.find(t => t.id === parseInt(travelId));
                const color = getPersonColor(travelId);

                // Check if person has a car assigned (show car icon)
                const hasCar = !!travel?.car_id;
                // Check if this person picks up others (has passengers)
                const hasPassengers = travel?.passengers_data && travel.passengers_data.length > 0;
                // Pickup mode: check if this travel is a driver or passenger
                const isDriver = hasPassengers; // Has passengers to pick up
                const isPassenger = !!travel?.pickup_driver_id;
                const isSelectedDriver = pickupMode && selectedDriverId === parseInt(travelId);
                const canBePassenger = pickupMode && selectedDriverId && selectedDriverId !== parseInt(travelId) && !isDriver && !isPassenger;

                // Show car icon only if has car assigned OR has passengers
                const showCarIcon = hasCar || hasPassengers;

                // Calculate offset if multiple people at same location
                const key = `${location.lat.toFixed(4)}_${location.lon.toFixed(4)}`;
                const count = locationCounts[key];
                const index = locationIndices[key]++;

                let offsetLat = 0;
                let offsetLon = 0;

                if (count > 1) {
                  // Spread markers in a circle around the point
                  const angle = (index / count) * 2 * Math.PI;
                  const radius = 0.0008; // Small offset ~80m
                  offsetLat = Math.cos(angle) * radius;
                  offsetLon = Math.sin(angle) * radius;
                }

                // Handle marker click in pickup mode
                const handleMarkerClick = () => {
                  if (!pickupMode) return;

                  if (!selectedDriverId) {
                    // No driver selected yet - select this as driver (if not already a passenger)
                    if (!isPassenger && onSelectDriver) {
                      onSelectDriver(parseInt(travelId));
                    }
                  } else if (selectedDriverId === parseInt(travelId)) {
                    // Clicked on selected driver - deselect
                    if (onSelectDriver) {
                      onSelectDriver(null);
                    }
                  } else if (canBePassenger) {
                    // Clicked on potential passenger - set pickup relationship
                    if (onSetPickupDriver) {
                      onSetPickupDriver(parseInt(travelId), selectedDriverId);
                    }
                  }
                };

                return (
                  <React.Fragment key={travelId}>
                    <Marker
                      position={[location.lat + offsetLat, location.lon + offsetLon]}
                      icon={createPersonIcon(
                        showCarIcon ? '#16a34a' : isPassenger ? '#f97316' : color,
                        showCarIcon,
                        isPassenger,
                        isSelectedDriver
                      )}
                      eventHandlers={{
                        click: handleMarkerClick
                      }}
                    >
                    <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                      <span className="font-medium">
                        {showCarIcon ? '🚗' : isPassenger ? '🧑' : '👤'} {location.name}
                      </span>
                    </Tooltip>
                    <Popup>
                      <div className="p-1">
                        <h3 className="font-bold text-blue-700 mb-1">
                          {showCarIcon ? '🚗' : isPassenger ? '🧑' : '👤'} {location.name}
                        </h3>
                        <p className="text-xs text-gray-600 mb-2">{location.address}</p>
                        {hasPassengers && travel?.passengers_data && (
                          <div className="text-xs text-green-600 mb-2">
                            <strong>Picks up:</strong>
                            <ul className="ml-2">
                              {travel.passengers_data.map(p => (
                                <li key={p.id}>• {p.user_name}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {isPassenger && travel?.pickup_driver_data && (
                          <p className="text-xs text-orange-600 mb-2">
                            <strong>Picked up by:</strong> {travel.pickup_driver_data.user_name}
                          </p>
                        )}
                        {projectLocation && (
                          <p className="text-xs text-gray-500">
                            📏 Distance to project: {calculateDistance(
                              location.lat, location.lon,
                              projectLocation.lat, projectLocation.lon
                            ).toFixed(0)} km
                          </p>
                        )}
                        {/* Pickup mode controls */}
                        {pickupMode && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            {!selectedDriverId && !isPassenger && !isDriver && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleMarkerClick(); }}
                                className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 w-full"
                              >
                                Select as Driver
                              </button>
                            )}
                            {isSelectedDriver && (
                              <span className="text-xs text-amber-600 font-medium">
                                ✓ Selected as driver - click passengers
                              </span>
                            )}
                            {canBePassenger && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleMarkerClick(); }}
                                className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 w-full"
                              >
                                Add as Passenger
                              </button>
                            )}
                          </div>
                        )}

                        {/* Remove pickup buttons - always visible when applicable */}
                        {(isPassenger || isDriver) && onClearPickupDriver && (
                          <div className={`mt-2 pt-2 border-t border-gray-200 ${pickupMode ? '' : ''}`}>
                            {isPassenger && (
                              <button
                                onClick={(e) => { e.stopPropagation(); onClearPickupDriver(parseInt(travelId)); }}
                                className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 w-full"
                              >
                                Remove from pickup route
                              </button>
                            )}
                            {isDriver && travel?.passengers_data && travel.passengers_data.length > 0 && (
                              <div className="space-y-1">
                                <span className="text-xs text-gray-500">Remove passengers:</span>
                                {travel.passengers_data.map(p => (
                                  <button
                                    key={p.id}
                                    onClick={(e) => { e.stopPropagation(); onClearPickupDriver(p.id); }}
                                    className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 w-full flex items-center justify-between"
                                  >
                                    <span>{p.user_name}</span>
                                    <span>✕</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>

                  {/* Full route for driver: driver -> passengers -> project (hide in full map view) */}
                  {!isFullMapView && isDriver && projectLocation && travel?.passengers_data && (
                    <Polyline
                      positions={(() => {
                        const routePoints = [];
                        // Start from driver's location
                        routePoints.push([location.lat, location.lon]);
                        // Add all passengers' locations
                        travel.passengers_data.forEach(passenger => {
                          const passengerLoc = personLocations[passenger.id];
                          if (passengerLoc) {
                            routePoints.push([passengerLoc.lat, passengerLoc.lon]);
                          }
                        });
                        // End at project
                        routePoints.push([projectLocation.lat, projectLocation.lon]);
                        return routePoints;
                      })()}
                      color="#16a34a"
                      weight={4}
                      opacity={0.8}
                    />
                  )}

                  {/* Line to project for non-drivers who are not passengers - CAR mode only (hide in full map view) */}
                  {!isFullMapView && transportType === 'car' && projectLocation && !isDriver && !isPassenger && (
                    <Polyline
                      positions={[
                        [location.lat, location.lon],
                        [projectLocation.lat, projectLocation.lon]
                      ]}
                      color={color}
                      weight={2}
                      opacity={0.6}
                      dashArray="5, 10"
                    />
                  )}

                  {/* Passengers don't get their own line to project - they're part of driver's route */}
                  </React.Fragment>
                );
              });
            })()}
          </MapContainer>
        )}
      </div>

      {/* Pickup Route Builder - CAR mode, hidden in full map view */}
      {transportType === 'car' && !isFullMapView && (
        <div className="p-2 bg-gray-50 border-t border-gray-200 max-h-64 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-xs font-medium text-gray-700">
              Pickup Route Builder
              <span className="text-gray-400 font-normal ml-1">
                (1st = driver, next = passengers)
              </span>
            </h5>
            {customRoute.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={clearCustomRoute}
                  className="text-xs text-red-600 hover:text-red-700"
                >
                  Clear
                </button>
                {customRoute.length >= 2 && onSetPickupDriver && (
                  <button
                    onClick={() => {
                      // First person in route is driver, rest are passengers
                      const driverId = customRoute[0];
                      const passengerIds = customRoute.slice(1);
                      passengerIds.forEach(passengerId => {
                        onSetPickupDriver(passengerId, driverId);
                      });
                      clearCustomRoute();
                    }}
                    className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Save Route
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Current pickup route visualization */}
          {customRoute.length > 0 && (
            <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded">
              <div className="text-xs text-green-700 mb-1 font-medium">Route Preview:</div>
              <div className="flex items-center flex-wrap gap-1 text-xs">
                {customRoute.map((travelId, idx) => {
                  const travel = selectedTravels.find(t => t.id === travelId);
                  const name = travel ? (getUserName ? getUserName(travel) : `User ${travel.user_id}`) : '?';
                  const isDriverInRoute = idx === 0;
                  return (
                    <React.Fragment key={travelId}>
                      <span className={`px-2 py-0.5 rounded ${isDriverInRoute ? 'bg-green-600 text-white' : 'bg-orange-100 text-orange-700'}`}>
                        {isDriverInRoute ? '🚗' : '🧑'} {name}
                      </span>
                      {idx < customRoute.length - 1 && <span className="text-gray-400">→</span>}
                    </React.Fragment>
                  );
                })}
                <span className="text-gray-400">→</span>
                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">🏗️ Project</span>
              </div>
            </div>
          )}

          {/* People list */}
          <div className="space-y-1">
            {/* People already in route - with reorder controls */}
            {customRoute.map((travelId, idx) => {
              const travel = selectedTravels.find(t => t.id === travelId);
              if (!travel) return null;
              const location = personLocations[travel.id];
              const name = getUserName ? getUserName(travel) : `User ${travel.user_id}`;
              const isDriverInRoute = idx === 0;

              let distance = null;
              if (location && projectLocation) {
                distance = calculateDistance(
                  location.lat, location.lon,
                  projectLocation.lat, projectLocation.lon
                );
              }

              return (
                <div
                  key={travel.id}
                  className={`flex items-center justify-between text-xs p-2 rounded border ${
                    isDriverInRoute
                      ? 'bg-green-100 border-green-400'
                      : 'bg-orange-50 border-orange-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white font-bold ${
                      isDriverInRoute ? 'bg-green-600' : 'bg-orange-500'
                    }`}>
                      {idx + 1}
                    </div>
                    <span className="text-lg">{isDriverInRoute ? '🚗' : '🧑'}</span>
                    <span className="font-medium">{name}</span>
                    {distance !== null && (
                      <span className="text-gray-500">({distance.toFixed(0)} km)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Move up */}
                    {idx > 0 && (
                      <button
                        onClick={() => moveUp(travelId)}
                        className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                        title="Move up"
                      >
                        ↑
                      </button>
                    )}
                    {/* Move down */}
                    {idx < customRoute.length - 1 && (
                      <button
                        onClick={() => moveDown(travelId)}
                        className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                        title="Move down"
                      >
                        ↓
                      </button>
                    )}
                    {/* Remove */}
                    <button
                      onClick={() => togglePersonInRoute(travelId)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 rounded"
                      title="Remove from route"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}

            {/* People not in route - can be added */}
            {selectedTravels
              .filter(travel => !customRoute.includes(travel.id))
              .map(travel => {
                const location = personLocations[travel.id];
                const status = geocodingStatus[travel.id];
                const name = getUserName ? getUserName(travel) : `User ${travel.user_id}`;

                // Check if already has pickup assignment
                const hasPickupDriver = !!travel.pickup_driver_id;
                const hasPassengers = travel.passengers_data && travel.passengers_data.length > 0;

                let distance = null;
                if (location && projectLocation) {
                  distance = calculateDistance(
                    location.lat, location.lon,
                    projectLocation.lat, projectLocation.lon
                  );
                }

                return (
                  <div
                    key={travel.id}
                    className={`flex items-center justify-between text-xs p-2 rounded border bg-white ${
                      !location ? 'opacity-50' : 'hover:bg-gray-50'
                    } ${hasPickupDriver ? 'border-orange-200' : hasPassengers ? 'border-green-200' : 'border-gray-200'}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">
                        {hasPassengers ? '🚗' : hasPickupDriver ? '🧑' : '👤'}
                      </span>
                      <span className="font-medium">{name}</span>
                      {distance !== null && (
                        <span className="text-gray-500">({distance.toFixed(0)} km)</span>
                      )}
                      {hasPickupDriver && travel.pickup_driver_data && (
                        <span className="text-orange-600 text-xs">
                          ← picked by {travel.pickup_driver_data.user_name}
                        </span>
                      )}
                      {hasPassengers && (
                        <span className="text-green-600 text-xs">
                          picks {travel.passengers_data.length} person(s)
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {status === 'not_found' && (
                        <span className="text-amber-600">No GPS</span>
                      )}
                      {location && !hasPickupDriver && !hasPassengers && (
                        <button
                          onClick={() => togglePersonInRoute(travel.id)}
                          className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded border border-blue-200"
                        >
                          + Add
                        </button>
                      )}
                      {(hasPickupDriver || hasPassengers) && onClearPickupDriver && (
                        <button
                          onClick={() => {
                            if (hasPickupDriver) {
                              onClearPickupDriver(travel.id);
                            } else if (hasPassengers) {
                              // Clear all passengers
                              travel.passengers_data.forEach(p => onClearPickupDriver(p.id));
                            }
                          }}
                          className="px-2 py-1 text-red-600 hover:bg-red-50 rounded border border-red-200"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Flight info panel - PLANE mode, hidden in full map view */}
      {transportType === 'plane' && !isFullMapView && (
        <div className="p-2 bg-purple-50 border-t border-purple-200 max-h-80 overflow-y-auto">
          <h5 className="text-xs font-medium text-purple-700 mb-2 flex items-center gap-1">
            <Plane className="h-3 w-3" />
            Flight Route
          </h5>

          {/* Warning if airports not selected */}
          {(!departureAirport || !destinationAirport) && (
            <div className="bg-amber-50 rounded border border-amber-200 p-2 mb-2">
              <div className="text-xs text-amber-700 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Select departure and destination airports in the form on the left
              </div>
            </div>
          )}

          {/* Flight Route Visualization - Full Journey */}
          {airportInfo && airportInfo.departureAirport && airportInfo.destinationAirport && (
            <div className="bg-white rounded border border-purple-200 p-2 mb-2">
              <div className="text-xs font-medium text-gray-600 mb-2 text-center">
                🗺️ {travelType === 'check_out' ? 'Return Journey' : 'Full Journey'}
                {organizedTransport && <span className="ml-1 text-green-600">(Organized Transport)</span>}
              </div>
              <div className="flex items-center justify-center gap-1 text-sm flex-wrap">
                {/* For check_in: Home → (Meeting Point/Zbiórka) → Departure → Destination → Dropoff/Project */}
                {/* For check_out: Project → Departure → Destination → (Pickup) → Dropoff/Home */}

                {travelType === 'check_in' ? (
                  <>
                    {/* Home */}
                    <div className="text-center">
                      <div className="text-blue-600 font-bold text-xs">🏠</div>
                      <div className="text-xs text-gray-500">Home</div>
                    </div>

                    {/* Organized transport: Home → Meeting Point → Departure Airport */}
                    {organizedTransport ? (
                      <>
                        {pickupPoint && (
                          <>
                            {/* Arrow to meeting point */}
                            <div className="flex items-center">
                              <div className="h-px bg-purple-300 w-3"></div>
                              <div className="text-purple-400 text-xs">🚗</div>
                              <div className="h-px bg-purple-300 w-3"></div>
                            </div>
                            {/* Meeting Point */}
                            <div className="text-center">
                              <div className="text-amber-600 font-bold text-xs">📍</div>
                              <div className="text-xs text-gray-500 max-w-16 truncate">Meeting</div>
                            </div>
                            {/* Arrow to departure (organized) */}
                            <div className="flex items-center">
                              <div className="h-px bg-amber-300 w-3"></div>
                              <div className="text-amber-500 text-xs">🚐</div>
                              <div className="h-px bg-amber-300 w-3"></div>
                            </div>
                          </>
                        )}
                        {!pickupPoint && (
                          /* No meeting point - direct to airport */
                          <div className="flex items-center">
                            <div className="h-px bg-purple-300 w-3"></div>
                            <div className="text-purple-400 text-xs">🚗</div>
                            <div className="h-px bg-purple-300 w-3"></div>
                          </div>
                        )}
                      </>
                    ) : (
                      /* No organized transport - direct to airport */
                      <div className="flex items-center">
                        <div className="h-px bg-purple-300 w-3"></div>
                        <div className="text-purple-400 text-xs">🚗</div>
                        <div className="h-px bg-purple-300 w-3"></div>
                      </div>
                    )}

                    {/* Departure airport */}
                    <div className="text-center">
                      <div className="text-purple-700 font-bold">{airportInfo.departureAirport.code}</div>
                      <div className="text-xs text-gray-500">🛫</div>
                    </div>
                    {/* Flight */}
                    <div className="flex items-center">
                      <div className="h-px bg-purple-400 w-4"></div>
                      <div className="px-1 flex items-center gap-1 text-purple-600">
                        <Plane className="h-3 w-3" />
                      </div>
                      <div className="h-px bg-purple-400 w-4"></div>
                    </div>
                    {/* Destination airport */}
                    <div className="text-center">
                      <div className="text-red-700 font-bold">{airportInfo.destinationAirport.code}</div>
                      <div className="text-xs text-gray-500">🛬</div>
                    </div>

                    {/* After arrival: Destination → Dropoff/Project */}
                    {organizedTransport && dropoffLocation ? (
                      <>
                        <div className="flex items-center">
                          <div className="h-px bg-green-300 w-3"></div>
                          <div className="text-green-500 text-xs">🚐</div>
                          <div className="h-px bg-green-300 w-3"></div>
                        </div>
                        <div className="text-center">
                          <div className="text-green-600 font-bold text-xs">🏁</div>
                          <div className="text-xs text-gray-500 max-w-16 truncate">Dropoff</div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Arrow to project */}
                        <div className="flex items-center">
                          <div className="h-px bg-green-300 w-3"></div>
                          <div className="text-green-500 text-xs">🚗</div>
                          <div className="h-px bg-green-300 w-3"></div>
                        </div>
                        {/* Project */}
                        <div className="text-center">
                          <div className="text-green-700 font-bold text-xs">🏗️</div>
                          <div className="text-xs text-gray-500">Project</div>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  /* check_out journey */
                  <>
                    {/* Project/Start */}
                    <div className="text-center">
                      <div className="text-green-700 font-bold text-xs">🏗️</div>
                      <div className="text-xs text-gray-500">Project</div>
                    </div>
                    {/* Arrow to departure */}
                    <div className="flex items-center">
                      <div className="h-px bg-purple-300 w-3"></div>
                      <div className="text-purple-400 text-xs">🚗</div>
                      <div className="h-px bg-purple-300 w-3"></div>
                    </div>
                    {/* Departure airport */}
                    <div className="text-center">
                      <div className="text-purple-700 font-bold">{airportInfo.departureAirport.code}</div>
                      <div className="text-xs text-gray-500">🛫</div>
                    </div>
                    {/* Flight */}
                    <div className="flex items-center">
                      <div className="h-px bg-purple-400 w-4"></div>
                      <div className="px-1 flex items-center gap-1 text-purple-600">
                        <Plane className="h-3 w-3" />
                      </div>
                      <div className="h-px bg-purple-400 w-4"></div>
                    </div>
                    {/* Destination airport */}
                    <div className="text-center">
                      <div className="text-red-700 font-bold">{airportInfo.destinationAirport.code}</div>
                      <div className="text-xs text-gray-500">🛬</div>
                    </div>
                    {/* Organized transport route */}
                    {organizedTransport ? (
                      <>
                        {pickupPoint && (
                          <>
                            <div className="flex items-center">
                              <div className="h-px bg-amber-300 w-3"></div>
                              <div className="text-amber-500 text-xs">🚐</div>
                              <div className="h-px bg-amber-300 w-3"></div>
                            </div>
                            <div className="text-center">
                              <div className="text-amber-600 font-bold text-xs">📍</div>
                              <div className="text-xs text-gray-500 max-w-16 truncate">Pickup</div>
                            </div>
                          </>
                        )}
                        {dropoffLocation && (
                          <>
                            <div className="flex items-center">
                              <div className="h-px bg-blue-300 w-3"></div>
                              <div className="text-blue-500 text-xs">🚐</div>
                              <div className="h-px bg-blue-300 w-3"></div>
                            </div>
                            <div className="text-center">
                              <div className="text-blue-600 font-bold text-xs">🏁</div>
                              <div className="text-xs text-gray-500 max-w-16 truncate">Dropoff</div>
                            </div>
                          </>
                        )}
                        {/* Home (if dropoff is not home) */}
                        {homeLocation && (
                          <>
                            <div className="flex items-center">
                              <div className="h-px bg-blue-200 w-3"></div>
                              <div className="text-blue-400 text-xs">🚗</div>
                              <div className="h-px bg-blue-200 w-3"></div>
                            </div>
                            <div className="text-center">
                              <div className="text-blue-600 font-bold text-xs">🏠</div>
                              <div className="text-xs text-gray-500">Home</div>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Arrow to home */}
                        <div className="flex items-center">
                          <div className="h-px bg-blue-300 w-3"></div>
                          <div className="text-blue-500 text-xs">🚗</div>
                          <div className="h-px bg-blue-300 w-3"></div>
                        </div>
                        {/* Home */}
                        <div className="text-center">
                          <div className="text-blue-600 font-bold text-xs">🏠</div>
                          <div className="text-xs text-gray-500">Home</div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
              {/* Distances summary */}
              <div className="flex justify-center gap-4 mt-2 text-xs text-gray-600 flex-wrap">
                {airportInfo.flightDistance && (
                  <span>✈️ ~{Math.ceil(airportInfo.flightDistance / 800)}h flight</span>
                )}
                {organizedTransport && pickupPoint && (
                  <span className="text-amber-600">🚐 Organized transport</span>
                )}
              </div>
            </div>
          )}

          {/* Passengers and their distances to departure airport - only for check_in */}
          {travelType === 'check_in' && airportInfo && airportInfo.departureAirport && selectedTravels.length > 0 && (
            <div className="bg-white rounded border border-purple-200 p-2 mb-2">
              <div className="text-xs font-medium text-gray-700 mb-2">
                👥 Passengers ({selectedTravels.length}) - Distance to {airportInfo.departureAirport.code}
              </div>
              <div className="space-y-1">
                {selectedTravels.map(travel => {
                  const distanceToAirport = airportInfo.personDistancesToAirport?.[travel.id];
                  const name = getUserName ? getUserName(travel) : `User ${travel.user_id}`;
                  return (
                    <div key={travel.id} className="flex items-center justify-between text-xs bg-purple-50 rounded px-2 py-1">
                      <span className="font-medium">{name}</span>
                      {distanceToAirport !== null && distanceToAirport !== undefined ? (
                        <span className="text-purple-600">
                          🚗 ~{distanceToAirport.toFixed(0)} km to airport
                        </span>
                      ) : (
                        <span className="text-gray-400">No address</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* For check_in: show organized transport details if enabled */}
          {travelType === 'check_in' && organizedTransport && (pickupPoint || dropoffLocation) && (
            <div className="bg-white rounded border border-amber-200 p-2 mb-2">
              <div className="text-xs font-medium text-amber-700 mb-2">
                🚐 Organized Transport Details
              </div>
              <div className="space-y-1 text-xs">
                {pickupPoint && (
                  <div className="flex items-center gap-2 bg-amber-50 rounded px-2 py-1">
                    <span className="text-amber-600">📍</span>
                    <span className="font-medium">Meeting Point:</span>
                    <span className="text-gray-600 truncate">{pickupPoint}</span>
                  </div>
                )}
                {dropoffLocation && (
                  <div className="flex items-center gap-2 bg-green-50 rounded px-2 py-1">
                    <span className="text-green-600">🏁</span>
                    <span className="font-medium">Dropoff:</span>
                    <span className="text-gray-600 truncate">{dropoffLocation}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* For check_out: show organized transport details if enabled */}
          {travelType === 'check_out' && organizedTransport && (pickupPoint || dropoffLocation) && (
            <div className="bg-white rounded border border-green-200 p-2 mb-2">
              <div className="text-xs font-medium text-green-700 mb-2">
                🚐 Organized Transport Details
              </div>
              <div className="space-y-1 text-xs">
                {pickupPoint && (
                  <div className="flex items-center gap-2 bg-amber-50 rounded px-2 py-1">
                    <span className="text-amber-600">📍</span>
                    <span className="font-medium">Pickup:</span>
                    <span className="text-gray-600 truncate">{pickupPoint}</span>
                  </div>
                )}
                {dropoffLocation && (
                  <div className="flex items-center gap-2 bg-green-50 rounded px-2 py-1">
                    <span className="text-green-600">🏁</span>
                    <span className="font-medium">Dropoff:</span>
                    <span className="text-gray-600 truncate">{dropoffLocation}</span>
                  </div>
                )}
                {homeLocation && (
                  <div className="flex items-center gap-2 bg-blue-50 rounded px-2 py-1">
                    <span className="text-blue-600">🏠</span>
                    <span className="font-medium">Then to home:</span>
                    <span className="text-gray-500 text-xs">(own transport)</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Flight summary */}
          {airportInfo && airportInfo.departureAirport && airportInfo.destinationAirport && (
            <div className="p-2 bg-purple-100 rounded border border-purple-300">
              <div className="text-xs text-purple-700 font-medium mb-1">📊 Flight Summary</div>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div>
                  <span className="text-gray-600">From:</span>
                  <div className="font-bold text-purple-700">{airportInfo.departureAirport.code}</div>
                </div>
                <div>
                  <span className="text-gray-600">To:</span>
                  <div className="font-bold text-red-700">{airportInfo.destinationAirport.code}</div>
                </div>
                <div>
                  <span className="text-gray-600">Passengers:</span>
                  <div className="font-bold text-purple-700">{selectedTravels.length}</div>
                </div>
                <div>
                  <span className="text-gray-600">Flight:</span>
                  <div className="font-bold text-purple-700">
                    ~{airportInfo.flightDistance ? Math.ceil(airportInfo.flightDistance / 800) : '?'}h
                  </div>
                </div>
              </div>
              <div className="text-xs text-purple-600 mt-2 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Remember to upload flight tickets in Travel Coordination
              </div>
            </div>
          )}

        </div>
      )}

      {/* Route Optimization - CAR mode only, hidden in full map view */}
      {transportType === 'car' && !isFullMapView && projectLocation && Object.keys(personLocations).length >= 1 && (
        <div className="p-2 bg-gray-50 border-t border-gray-200">
          <div className={`mt-2 p-2 rounded border ${useCustomRoute && customRoute.length > 0 ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-center justify-between mb-2">
              <p className={`text-xs font-medium ${useCustomRoute && customRoute.length > 0 ? 'text-blue-700' : 'text-green-700'}`}>
                {useCustomRoute && customRoute.length > 0 ? '📍 Your Custom Route' : '🚗 Suggested Pickup Route'}
              </p>
              {!useCustomRoute && customRoute.length === 0 && (
                <span className="text-xs text-gray-500">or click people above</span>
              )}
            </div>
            {(() => {
              // Get all locations
              const allLocations = Object.entries(personLocations).map(([id, loc]) => ({
                id: parseInt(id),
                ...loc,
                travel: selectedTravels.find(t => t.id === parseInt(id))
              }));

              if (allLocations.length === 0) return <p className="text-xs text-gray-500">No locations found</p>;

              let route = [];
              let totalDistance = 0;

              if (useCustomRoute && customRoute.length > 0) {
                // Use custom route
                route = customRoute
                  .map(id => allLocations.find(loc => loc.id === id))
                  .filter(Boolean);
              } else {
                // Use suggested route (farthest first)
                route = [...allLocations].sort((a, b) => {
                  const distA = calculateDistance(a.lat, a.lon, projectLocation.lat, projectLocation.lon);
                  const distB = calculateDistance(b.lat, b.lon, projectLocation.lat, projectLocation.lon);
                  return distB - distA;
                });
              }

              // Road distance correction factor (roads are ~30% longer than straight line)
              const roadFactor = 1.3;

              // Calculate total distance (one way)
              let prevLoc = null;
              route.forEach((loc) => {
                if (prevLoc) {
                  totalDistance += calculateDistance(prevLoc.lat, prevLoc.lon, loc.lat, loc.lon);
                }
                prevLoc = loc;
              });

              if (prevLoc) {
                totalDistance += calculateDistance(prevLoc.lat, prevLoc.lon, projectLocation.lat, projectLocation.lon);
              }

              // Apply road correction factor
              totalDistance = totalDistance * roadFactor;

              // Calculate savings vs separate cars (one way only)
              const separateDistance = allLocations.reduce((sum, loc) => {
                return sum + calculateDistance(loc.lat, loc.lon, projectLocation.lat, projectLocation.lon) * roadFactor;
              }, 0);

              const savings = separateDistance - totalDistance;

              // Estimate travel time (avg 50 km/h for mixed roads with stops, traffic)
              const avgSpeedKmh = 50;
              const totalTimeHours = totalDistance / avgSpeedKmh;
              const hours = Math.floor(totalTimeHours);
              const minutes = Math.round((totalTimeHours - hours) * 60);
              const timeStr = hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`;

              // Calculate arrival time if departure time is set
              let arrivalStr = null;
              if (departureTime) {
                const [depHour, depMin] = departureTime.split(':').map(Number);
                const totalMinutes = depHour * 60 + depMin + hours * 60 + minutes;
                const arrHour = Math.floor(totalMinutes / 60) % 24;
                const arrMin = totalMinutes % 60;
                arrivalStr = `${arrHour.toString().padStart(2, '0')}:${arrMin.toString().padStart(2, '0')}`;
              }

              return (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-1 text-xs">
                    {route.map((loc, idx) => (
                      <React.Fragment key={loc.id}>
                        <span className={`px-2 py-1 rounded border font-medium ${useCustomRoute ? 'bg-blue-100 border-blue-300' : 'bg-white'}`} style={{ borderColor: useCustomRoute ? undefined : getPersonColor(loc.id) }}>
                          {idx + 1}. {getUserName ? getUserName(loc.travel) : loc.name}
                        </span>
                        {idx < route.length - 1 && <span className={useCustomRoute ? 'text-blue-600' : 'text-green-600'}>→</span>}
                      </React.Fragment>
                    ))}
                    <span className={useCustomRoute ? 'text-blue-600' : 'text-green-600'}>→</span>
                    <span className="bg-green-100 px-2 py-1 rounded border border-green-300 font-medium">
                      🏗️ Project
                    </span>
                  </div>
                  <div className={`flex flex-wrap justify-between gap-2 text-xs ${useCustomRoute ? 'text-blue-700' : 'text-green-700'}`}>
                    <span>📏 ~{totalDistance.toFixed(0)} km</span>
                    <span>⏱️ ~{timeStr}</span>
                    {savings > 5 && (
                      <span className="font-medium text-green-600">💰 Save ~{savings.toFixed(0)} km vs separate</span>
                    )}
                  </div>
                  {arrivalStr && (
                    <div className="text-xs text-gray-600 bg-white rounded px-2 py-1 border border-gray-200 flex items-center gap-2">
                      <span>🚗 Departure: <strong>{departureTime}</strong></span>
                      <span>→</span>
                      <span>🏁 Est. arrival: <strong>~{arrivalStr}</strong></span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelMapPanel;
